import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useRef } from "react";
import { AnnotationSelectionChrome } from "../annotation-selection-chrome";
export const DrawingSelectionView = memo(function DrawingSelectionView({ annotationId, frame, containerRef, onPatch, onSettle, }) {
    // Snapshot at gesture start. The chrome emits a new absolute scale
    // on each move; we re-apply it as a ratio to BOTH width and height
    // so the drawing scales uniformly. Captured per-render via
    // `useRef`-as-mutable-ref so the snapshot is "the values when the
    // user pressed down" — not the live frame, which is mid-update.
    const startSizeRef = useRef(null);
    // The chrome's `scale` axis is "the bbox width" — same number,
    // different name. Width/height are kept proportional via the
    // ratio math below. Height could equally have been the axis;
    // width is conventional.
    const handleScale = useCallback((next) => {
        // First move of a fresh resize gesture: snapshot the current
        // bbox so subsequent moves' ratios apply to the SAME starting
        // size. The chrome's `onSettle` clears this ref so the next
        // gesture starts fresh.
        if (startSizeRef.current === null) {
            startSizeRef.current = { width: frame.width, height: frame.height };
        }
        const start = startSizeRef.current;
        const ratio = next / start.width;
        onPatch({
            width: next,
            height: start.height * ratio,
        });
    }, [frame.width, frame.height, onPatch]);
    const handleRotation = useCallback((next) => {
        onPatch({ rotation: next });
    }, [onPatch]);
    const handleSettle = useCallback(() => {
        startSizeRef.current = null;
        onSettle();
    }, [onSettle]);
    // The chrome lives inside a host div sized + positioned to match
    // the drawing's bbox. Same `translate(-50%, -50%) rotate(R)` as
    // `DrawingAnnotationView` so the outline + handles sit precisely
    // over the drawing no matter how it's rotated.
    const wrapperStyle = {
        position: "absolute",
        left: `${frame.x * 100}%`,
        top: `${frame.y * 100}%`,
        width: `${frame.width * 100}%`,
        height: `${frame.height * 100}%`,
        transform: `translate(-50%, -50%) rotate(${frame.rotation}deg)`,
        pointerEvents: "none",
    };
    return (_jsx("div", { style: wrapperStyle, "aria-hidden": true, children: _jsx(AnnotationSelectionChrome, { annotationId: annotationId, containerRef: containerRef, centerX: frame.x, centerY: frame.y, scale: frame.width, rotation: frame.rotation, onScale: handleScale, onRotation: handleRotation, onSettle: handleSettle }) }));
});
